import { useAppQuery } from 'core/query/hooks/useAppQuery';
import { TargetService } from '@constants';
import { getIntegrationHealth } from 'services';
import { IntegrationHealth } from 'types';
import { integrationQueryKeys } from 'IntegrationModule/api/queries/integrationQueryKeys';
import { formatIntegrationHealthDateParams } from 'utils';
import { subDays } from 'date-fns';
import { useMemo } from 'react';

export type AggregateHealthDay = {
  date: string; // YYYY-MM-DD
  requestCount: number;
  successCount: number;
  successRate: number; // 0-100
  // Number of distinct `targetServiceId` values that emitted a health record
  // on this date. We count integrations, not teams — a single team can in
  // principle have multiple integrations for the same service (each gets its
  // own targetServiceId), so the integration count is what's actually being
  // measured. The calendar tooltip labels this "Active integrations".
  integrationCount: number;
  errors: Record<string, number>;
};

type RawHealthRow = IntegrationHealth & { targetServiceId?: number };

type GetIntegrationHealthResponse = { data: RawHealthRow[] | Record<string, unknown> };

const mergeErrors = (
  a: Record<string, number>,
  b: Record<string, number> | undefined,
): Record<string, number> => {
  if (!b) return a;
  const out: Record<string, number> = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (typeof v !== 'number') continue;
    out[k] = (out[k] ?? 0) + v;
  }
  return out;
};

const aggregateRowsByDate = (rows: RawHealthRow[]): AggregateHealthDay[] => {
  const byDate = new Map<
    string,
    {
      requestCount: number;
      successCount: number;
      targetServiceIds: Set<number>;
      errors: Record<string, number>;
    }
  >();

  for (const r of rows) {
    if (!r?.date) continue;
    const bucket = byDate.get(r.date) ?? {
      requestCount: 0,
      successCount: 0,
      targetServiceIds: new Set<number>(),
      errors: {},
    };
    bucket.requestCount += r.requestCount ?? 0;
    bucket.successCount += r.successCount ?? 0;
    if (typeof r.targetServiceId === 'number') bucket.targetServiceIds.add(r.targetServiceId);
    bucket.errors = mergeErrors(bucket.errors, r.errors as Record<string, number> | undefined);
    byDate.set(r.date, bucket);
  }

  return Array.from(byDate.entries())
    .map(([date, b]) => ({
      date,
      requestCount: b.requestCount,
      successCount: b.successCount,
      successRate: b.requestCount > 0 ? (b.successCount / b.requestCount) * 100 : 0,
      integrationCount: b.targetServiceIds.size,
      errors: b.errors,
    }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
};

/**
 * Fetches per-team, per-day health from the agent (via galaxy-server → IS →
 * agent proxy) for the last `days` days, then aggregates client-side into one
 * row per date. Powers the overview-page aggregate health calendar.
 *
 * Why client-side aggregation: the agent's getHealth already groups by
 * (targetServiceId, date) when called with targetServiceIds. Aggregating per
 * date in the browser avoids needing a new IS endpoint or a daily cron — see
 * the INTG-11603 ticket comments for context.
 */
export const useOverviewAggregateHealthQuery = ({
  targetService,
  targetServiceIds,
  days = 30,
}: {
  targetService: TargetService;
  targetServiceIds: number[];
  days?: number;
}) => {
  const today = useMemo(() => new Date(), []);
  const startDate = useMemo(
    () => formatIntegrationHealthDateParams(subDays(today, days - 1)),
    [today, days],
  );
  const endDate = useMemo(() => formatIntegrationHealthDateParams(today), [today]);

  const enabled = !!targetService && targetServiceIds.length > 0;

  // Sorted for stable identity — React Query compares keys structurally, and a
  // shuffled `targetServiceIds` should not bust the cache.
  const sortedTargetServiceIds = useMemo(
    () => [...targetServiceIds].sort((a, b) => a - b),
    [targetServiceIds],
  );

  const { data, ...query } = useAppQuery<AggregateHealthDay[]>({
    queryKey: integrationQueryKeys.list({
      identifier: 'useOverviewAggregateHealthQuery',
      targetService,
      targetServiceIds: sortedTargetServiceIds,
      startDate,
      endDate,
    }),
    queryFn: async ({ token }) => {
      // Send the same sorted array we keyed on so the fetch is a pure function
      // of the React Query key — two callers with the same set of IDs in
      // different orders hash to one cache entry AND issue identical requests.
      const response = (await getIntegrationHealth(
        {
          targetService,
          targetServiceIds: sortedTargetServiceIds,
          serviceNames: [targetService],
          startDate,
          endDate,
        },
        {},
        token,
      )) as GetIntegrationHealthResponse;

      const raw = Array.isArray(response?.data) ? (response.data as RawHealthRow[]) : [];
      return aggregateRowsByDate(raw);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    days: data ?? [],
    startDate,
    endDate,
    ...query,
  };
};
